import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Pagination,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../Api/ApiUrls";
import UserViewModal from "./UserViewModal";

const buyerCategories = [
  "PLOT", "LAND", "COMMERCIAL LAND/PLOT", "RENT WITH DUPLEX BUILDING", "DUPLEX HOUSE",
  "RENTAL BUILDING", "PG-OFFICES", "FLAT", "VILLA", "COMMERCIAL BUILDING", "APARTMENT"
];

const rentCategories = [
  "1BHK", "2BHK", "3BHK", "4+BHK", "PLOT", "LAND", "DUPLEX HOUSE",
  "COMMERCIAL LAND", "COMMERCIAL BUILDING/Space", "VILLA",
  "PG-SCHOOL-OFFICE", "SHOPPING mall/shop"
];

const leaseCategories = [
  "1BHK", "2BHK", "3BHK", "4+BHK", "PLOT", "LAND", "DUPLEX HOUSE",
  "COMMERCIAL LAND", "COMMERCIAL BUILDING/Space", "VILLA",
  "PG-SCHOOL-OFFICE", "SHOPPING mall/shop"
];

const UserPropertiesTable = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // single source of truth
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [search, setSearch] = useState('');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [selectedRentalType, setSelectedRentalType] = useState('');
  const [selectedAdminStatus, setSelectedAdminStatus] = useState('All');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  // Refs for debouncing and aborting
  const requestControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const latestRequestId = useRef(0);

  const getPropertyType = () => {
    switch (activeTab) {
      case 0: return "sell";
      case 1: return "rent";
      case 2: return "lease";
      default: return "sell";
    }
  };

  const getRentalTypes = () => {
    switch (activeTab) {
      case 0: return buyerCategories;
      case 1: return rentCategories;
      case 2: return leaseCategories;
      default: return buyerCategories;
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedRentalType('');
  };

  // Core fetch. Always resolves with { data, pagination } or null on failure.
  const fetchProperties = async (page, size, searchQuery, rentalType, adminStatus) => {
    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      const type = getPropertyType();
      let url = `${BASE_URL}/property/?page=${page}&page_size=${size}&type=${type}`;
      // exclude records posted by Admin
      url += `&exclude_posted_by=${encodeURIComponent('Admin')}`;
      
      // Add Admin_status filter
      if (adminStatus && adminStatus !== 'All') {
        url += `&Admin_status=${encodeURIComponent(adminStatus)}`;
      } else if (adminStatus === 'All') {
        url += `&Admin_status=${encodeURIComponent('All')}`;
      }
      
      if (rentalType) {
        url += `&category_name=${encodeURIComponent(rentalType)}`;
      }

      if (searchQuery) {
        url += `&search_query=${encodeURIComponent(searchQuery)}`;
      }

      const res = await axios.get(url, { signal });
      
      // Prefer server-side exclusion; fallback to client-side filter by posted_by
      const allData = res.data?.data || [];
      const data = allData.filter(p => (p.posted_by || '').toLowerCase() !== 'admin');
      
      const pagination = res.data?.pagination || {};

      return { data, pagination };
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching properties:", error);
        toast.error("Failed to fetch properties");
      }
      return null;
    }
  };

  // Apply a successful fetch result to state, in one consistent place.
  const applyResult = (result, page) => {
    if (!result) return;
    const { data, pagination } = result;
    setProperties(data);
    setCurrentPage(pagination.current_page ?? page);
    setTotalPages(pagination.total_pages ?? 1);
    setTotalCount(pagination.total_count ?? data.length);
  };

  // Unified loader for page <= 100. Always: loading on, fetch, apply, loading off.
  const loadPage = async (page, size, searchQuery, rentalType, adminStatus) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    const result = await fetchProperties(page, size, searchQuery, rentalType, adminStatus);
    if (requestId !== latestRequestId.current) return; // a newer request superseded this one
    applyResult(result, page);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  // Progressive loader for page > 100, fetched in chunks of 100.
  const loadProgressively = async (page, size, searchQuery, rentalType, adminStatus) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    setProgressiveLoading(true);

    const totalChunks = Math.ceil(size / 100);
    let allProps = [];
    let lastPagination = {};

    for (let i = 0; i < totalChunks; i++) {
      const result = await fetchProperties(page, 100, searchQuery, rentalType, adminStatus);
      if (requestId !== latestRequestId.current) return; // superseded mid-flight
      if (result) {
        // Filter out admin properties from each chunk
        const filteredData = result.data.filter(p => (p.posted_by || '').toLowerCase() !== 'admin');
        allProps = [...allProps, ...filteredData];
        lastPagination = result.pagination;
        setProperties([...allProps]);
        if (i === 0) {
          // First chunk landed — table is no longer empty, drop the full-screen spinner.
          setIsLoading(false);
        }
      }
      if (i < totalChunks - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    }

    if (requestId !== latestRequestId.current) return;
    const recalculatedTotalPages = Math.ceil((lastPagination.total_count || 0) / size);
    setTotalPages(recalculatedTotalPages || 1);
    setTotalCount(lastPagination.total_count || allProps.length);
    setCurrentPage(page);
    setIsLoading(false);
    setProgressiveLoading(false);
    setHasLoadedOnce(true);
  };

  const runLoad = (page, size, searchQuery, rentalType, adminStatus) => {
    if (size > 100) {
      loadProgressively(page, size, searchQuery, rentalType, adminStatus);
    } else {
      loadPage(page, size, searchQuery, rentalType, adminStatus);
    }
  };

  // Tab switch OR page size change -> reset to page 1, reload (single effect, no duplicate fetch on mount)
  useEffect(() => {
    runLoad(1, pageSize, search, selectedRentalType, selectedAdminStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedRentalType, pageSize, selectedAdminStatus]);

  // Search -> debounce, reload current page
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      runLoad(1, pageSize, search, selectedRentalType, selectedAdminStatus);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const getImageUrl = (imagePath) => `${BASE_URL}${imagePath}`;

  const handleDelete = async (property_id) => {
    if (!window.confirm("Are you sure you want to delete this property?")) return;

    try {
      await axios.delete(`${BASE_URL}/property/${property_id}/`);
      toast.success("Property deleted successfully");
      runLoad(currentPage, pageSize, search, selectedRentalType, selectedAdminStatus);
    } catch (error) {
      console.error("Error deleting property:", error);
      toast.error("Failed to delete property. Please try again.");
    }
  };

  const handleStatusChange = async (propertyId, newStatus) => {
    try {
      setProperties(prev =>
        prev.map(property =>
          property.property_id === propertyId
            ? { ...property, Admin_status: newStatus }
            : property
        )
      );

      await axios.put(`${BASE_URL}/property/${propertyId}/`, {
        Admin_status: newStatus
      });

      toast.success("Status updated successfully!");
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status. Please try again.");
      runLoad(currentPage, pageSize, search, selectedRentalType, selectedAdminStatus);
    }
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleRentalTypeChange = (event, newValue) => {
    setSelectedRentalType(newValue);
  };

  const handleViewClick = (property) => {
    setCurrentProperty(property);
    setViewModalOpen(true);
  };

  const handlePageChange = (event, value) => {
    runLoad(value, pageSize, search, selectedRentalType, selectedAdminStatus);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleAdminStatusChange = (event) => {
    setSelectedAdminStatus(event.target.value);
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ maxWidth: 1500, mx: 'auto', p: 2, borderRadius: 2, boxShadow: 3, backgroundColor: '#fff' }}>
        <Typography variant="h4" sx={{ textAlign: 'center', color: 'primary.main', mb: 2 }}>
          User Uploaded Properties
        </Typography>

        <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} centered>
            <Tab label="Buyer Properties" />
            <Tab label="Rent Properties" />
            <Tab label="Lease Properties" />
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Search Properties"
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by name, location or price..."
              size="small"
              sx={{ width: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records</InputLabel>
              <Select
                value={pageSize}
                label="Records"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {progressiveLoading && <CircularProgress size={20} />}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Admin Status</InputLabel>
              <Select
                value={selectedAdminStatus}
                label="Admin Status"
                onChange={handleAdminStatusChange}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Rental Type Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedRentalType}
            onChange={handleRentalTypeChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="rental type tabs"
          >
            <Tab label="All Types" value="" />
            {getRentalTypes().map((type) => (
              <Tab key={type} label={type} value={type} />
            ))}
          </Tabs>
        </Box>

        {isLoading && properties.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {['S.No', 'Prop ID', 'Name', 'Images', 'User Details', 'Posted By', 'Rental Type', 'Facing', 'Site Area', 'Price', 'Location', 'Status', 'Actions'].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {properties.map((property, index) => (
                  <TableRow key={property.property_id}>
                    <TableCell align="center">
                      {(currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {`PROP${property.property_id}`}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{property.property_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{property.property_type}</Typography>
                    </TableCell>

                    <TableCell sx={{ textAlign: 'center', width: 120 }}>
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                        {property.property_images && property.property_images.length > 0 ? (
                          property.property_images.slice(0, 2).map((imageObj) => {
                            const imageUrl = getImageUrl(imageObj.image);
                            return (
                              <Box
                                key={imageObj.id}
                                sx={{ width: 60, height: 45, overflow: 'hidden', borderRadius: 1, border: '1px solid #ddd' }}
                              >
                                <Box
                                  component="img"
                                  src={imageUrl}
                                  alt=""
                                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => { e.target.src = "https://via.placeholder.com/60x45"; }}
                                />
                              </Box>
                            );
                          })
                        ) : <Typography variant="caption">No images</Typography>}
                      </Box>
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2">{property.user_first_name} {property.user_last_name}</Typography>
                      <Typography variant="caption" display="block">{property.user_mobile_no}</Typography>
                    </TableCell>

                    <TableCell align="center">{property.posted_by}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {property.category_name || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">{property.facing}</TableCell>
                    <TableCell align="center">{property.site_area}</TableCell>
                    <TableCell align="center">₹{property.price}</TableCell>
                    <TableCell align="center">{property.location}</TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      <Select
                        size="small"
                        value={property.Admin_status || "Pending"}
                        onChange={(e) =>
                          handleStatusChange(property.property_id, e.target.value)
                        }
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </Select>
                    </TableCell>

                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton color="info" size="small" onClick={() => handleViewClick(property)}>
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton color="error" size="small" onClick={() => handleDelete(property.property_id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {hasLoadedOnce && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {properties.length} of {totalCount} records
            </Typography>

            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={isLoading}
              />
            )}
          </Box>
        )}
      </Box>

      <UserViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        propertyId={currentProperty?.property_id}
      />
    </Container>
  );
};

export default UserPropertiesTable;